import { ReactElement, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { colors } from '~/styles/colors';
import {
  addMinutes, computeVentilationChart, VENT_CHART_X0, VENT_CHART_X1, VentilationWindow,
} from '../../ventilationChart';

/** Curva simulada do design (dados ilustrativos, não são leituras do DAM). */
const SIMULATED_CURVE = 'M 64,104.1 C 68.2,105.2 80.8,107.8 89.2,110.5 C 97.6,113.2 105.9,117.4 114.3,120 C 122.7,122.6 131.1,124.2 139.5,126.3 C 147.9,128.4 156.3,131.1 164.7,132.7 C 173.1,134.3 181.4,136.3 189.8,135.8 C 198.2,135.3 206.6,132.7 215,129.5 C 223.4,126.3 231.8,122.6 240.2,116.8 C 248.6,111.0 256.9,100.9 265.3,94.6 C 273.7,88.3 282.1,79.8 290.5,78.7 C 298.9,77.7 307.3,84.1 315.7,88.3 C 324.1,92.5 332.4,98.8 340.8,104.1 C 349.2,109.4 357.6,115.2 366,120 C 374.4,124.8 382.8,129.0 391.2,132.7 C 399.6,136.4 407.9,139.6 416.3,142.2 C 424.7,144.8 433.1,146.4 441.5,148.5 C 449.9,150.6 458.3,155.4 466.7,154.9 C 475.1,154.4 483.4,150.1 491.8,145.3 C 500.2,140.5 508.6,132.1 517,126.3 C 525.4,120.5 533.8,115.3 542.2,110.5 C 550.6,105.8 558.9,101.0 567.3,97.8 C 575.7,94.6 584.1,91.9 592.5,91.4 C 600.9,90.9 609.3,93.0 617.7,94.6 C 626.1,96.2 634.4,99.3 642.8,100.9 C 651.2,102.5 663.8,103.6 668,104.1';

const Y_LABELS = [
  { y: 59.7, offset: 2 },
  { y: 91.4, offset: 1 },
  { y: 123.1, offset: 0 },
  { y: 154.9, offset: -1 },
  { y: 186.6, offset: -2 },
];
const X_LABELS = [
  { x: 64, label: '00h' }, { x: 215, label: '06h' }, { x: 366, label: '12h' }, { x: 517, label: '18h' }, { x: 668, label: '24h' },
];

const SOFT = colors.text['sub-350'];
const PRIMARY = colors.brand.primary.base;
const INFO = colors.state.info.base;
const RED = colors.celsius['danger-base'];
const FONT = 'Inter, sans-serif';

interface VentilationChartProps {
  setpoint: number | null;
  ventilation: { start: number; end: number };
  schedule: VentilationWindow;
}

/** Gráfico ilustrativo do design: as faixas de ventilação acompanham os minutos e o horário da programação. */
export function VentilationChart({ setpoint, ventilation, schedule }: Readonly<VentilationChartProps>): ReactElement {
  const { t } = useTranslation();
  const geometry = computeVentilationChart(schedule, ventilation.start, ventilation.end);
  const progWidth = Math.max(0, geometry.progX2 - geometry.progX1);
  // O gráfico aparece duas vezes (na tela e ampliado): o id do recorte não pode repetir.
  const clipId = `eco-vent-prog-${useId().replace(/:/g, '')}`;
  const sp = setpoint ?? 24;
  const caption = t('ecoSimplificadoVentilacaoLegenda', {
    setpoint: sp,
    inicio: schedule.start,
    inicioFim: addMinutes(schedule.start, ventilation.start),
    fimInicio: addMinutes(schedule.end, -ventilation.end),
    fim: schedule.end,
  });

  return (
    <svg viewBox="0 0 700 352" preserveAspectRatio="xMidYMid meet" fontFamily={FONT}>
      <title>{t('ecoSimplificadoVentilacaoInicialFinal')}</title>
      <rect x="0" y="0" width="700" height="352" fill={colors.bg['white-0']} />
      <defs>
        <clipPath id={clipId}>
          <rect x={geometry.progX1} y="0" width={progWidth} height="352" />
        </clipPath>
      </defs>
      {/* Faixa confortável, setpoint e curva só dentro do horário da programação, como no design. */}
      <rect x={geometry.progX1} y="91.4" width={progWidth} height="63.5" fill={colors.brand.primary.lighter} />
      <line x1={geometry.progX1} y1="91.4" x2={geometry.progX2} y2="91.4" stroke={colors.brand.primary.light} />
      <line x1={geometry.progX1} y1="154.9" x2={geometry.progX2} y2="154.9" stroke={colors.brand.primary.light} />
      <line x1={geometry.progX1} y1="123.1" x2={geometry.progX2} y2="123.1" stroke={SOFT} strokeDasharray="5 4" />
      <line x1={VENT_CHART_X0} y1="59.7" x2={VENT_CHART_X1} y2="59.7" stroke={colors.celsius['grid-soft']} />
      <line x1={VENT_CHART_X0} y1="186.6" x2={VENT_CHART_X1} y2="186.6" stroke={colors.celsius['grid-soft']} />
      {Y_LABELS.map((item) => (
        <text key={item.y} x="52" y={item.y + 4} fontSize="12" fill={SOFT} textAnchor="end">{`${sp + item.offset}°`}</text>
      ))}
      <rect x={geometry.vent1X} y="40" width={geometry.vent1W} height="210" fill={INFO} opacity="0.16" />
      <rect x={geometry.vent2X} y="40" width={geometry.vent2W} height="210" fill={INFO} opacity="0.16" />
      <path d={SIMULATED_CURVE} clipPath={`url(#${clipId})`} fill="none" stroke={RED} strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      <line x1={geometry.progX1} y1="40" x2={geometry.progX1} y2="250" stroke={PRIMARY} strokeDasharray="4 4" />
      <line x1={geometry.progX2} y1="40" x2={geometry.progX2} y2="250" stroke={PRIMARY} strokeDasharray="4 4" />
      <line x1={VENT_CHART_X0} y1="250" x2={VENT_CHART_X1} y2="250" stroke={colors.stroke['soft-200']} />
      {X_LABELS.map((item) => (
        <text key={item.label} x={item.x} y="272" fontSize="12" fill={SOFT} textAnchor="middle">{item.label}</text>
      ))}
      <text x={(geometry.progX1 + geometry.progX2) / 2} y="242" fontSize="12" fontWeight="700" fill={PRIMARY} textAnchor="middle">
        {t('ecoSimplificadoEcoHabilitadoJanela', { inicio: schedule.start, fim: schedule.end })}
      </text>
      <line x1="64" y1="300" x2="88" y2="300" stroke={RED} strokeWidth="2.5" strokeLinecap="round" />
      <text x="96" y="304" fontSize="12" fill={colors.text['sub-500']}>{t('temperaturaAmbiente')}</text>
      <rect x="240" y="294" width="18" height="12" fill={colors.brand.primary.lighter} stroke={colors.brand.primary.light} />
      <text x="266" y="304" fontSize="12" fill={colors.text['sub-500']}>{t('ecoSimplificadoFaixaConfortavel')}</text>
      <rect x="380" y="294" width="18" height="12" fill={INFO} opacity=".5" />
      <text x="406" y="304" fontSize="12" fill={colors.text['sub-500']}>{t('ventilacao')}</text>
      <text x="64" y="336" fontSize="12" fill={SOFT}>{caption}</text>
    </svg>
  );
}
